/**
 * Display formatters for the pipeline UI.
 *
 * `formatDuration` existed as four near-copies across the pipeline components.
 * Three were identical; PipelineDashboard's variant deliberately drops the
 * seconds component (`5m` rather than `5m 30s`) to keep its table column
 * narrow, so it is intentionally NOT consolidated here — folding it in would
 * silently change the values shown in the runs list.
 */

import type { PipelineDetail } from '../services/pipelineOrchestrator';

export interface ConfigBadge {
  label: string;
  className: string;
}

/**
 * Non-default run settings worth surfacing next to a run's status.
 *
 * Deliberately omits `force_reprocess`: it is the default for every run, so
 * badging it fired on 100% of rows and carried no information. Dedup is the
 * inverse and the exception — it means collected VINs may be skipped rather
 * than reprocessed. `enable_dedup === !force_reprocess` on the wire and either
 * field may be the one present, so both spellings are checked.
 */
export function getConfigBadges(pipeline: PipelineDetail): ConfigBadge[] {
  const badges: ConfigBadge[] = [];

  if (pipeline.config?.enable_dedup === true || pipeline.config?.force_reprocess === false) {
    badges.push({
      label: 'Dedup Enabled',
      className: 'bg-amber-500/15 text-amber-500 border-amber-500/30'
    });
  }
  if (pipeline.config?.skip_vin_processing) {
    badges.push({
      label: 'VIN Processing Skipped',
      className: 'bg-muted text-muted-foreground border-muted'
    });
  }
  if ((pipeline.effective_run_mode || pipeline.run_mode) === 'delta') {
    badges.push({
      label: 'Delta Run',
      className: 'bg-blue-500/15 text-blue-500 border-blue-500/30'
    });
  }
  return badges;
}

/** `95` -> `1m 35s`. Null-safe; renders `-` for missing values. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

/** Absolute local timestamp. Null-safe. */
export function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) return '-';
  return new Date(ts).toLocaleString();
}

/**
 * Short relative time — `2m ago`, `3h ago`, `5d ago`.
 *
 * Hand-rolled rather than pulling date-fns into these components: the pipeline
 * surfaces only ever need this one coarse form, and pairing it with an absolute
 * `title` covers the precision case.
 */
export function formatRelativeTime(ts: string | null | undefined): string {
  if (!ts) return '-';
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return '-';

  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 0) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;

  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}
